/**
 * Dashboard do financeiro — layout da referência do PO (teste físico, item 35).
 *
 * De cima para baixo: cabeçalho com o atalho para o Ranking; busca; abas
 * Minha viagem · Família · Grupo; o card TOTAL GASTO com a fase da viagem e
 * os tiles A receber / A pagar; POR CATEGORIA com ícone, barra e valor;
 * DESPESAS RECENTES; o card "Resumo do grupo" (fora da aba Grupo); e os dois
 * CTAs. As listas mostram 3 e abrem em "VER TODAS".
 *
 * Não há mais nota de privacidade: despesa privada saiu do produto (item 34)
 * — o total do grupo É a soma das despesas.
 */
import { daysUntilStart, formatMoney, tripPhase } from '@nv/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAgora } from '../../demo/DemoContext';
import { useTrip } from '../../trip/TripContext';
import { tripInputDaViagem } from '../../trip/tripInput';
import { Icon } from '../../ui/icons';
import { Toast } from '../../ui/Toast';
import { Card, EmptyState, ErrorState, Segmented, Skeleton } from '../../ui/primitives';
import { listExpenses, readSummary } from './api';
import { categoriaDe, categoriasParaExibicao, escoposDisponiveis, type Escopo, filtrarDespesas, NOTA_DO_ESCOPO, saldoParaExibicao } from './model';
import { type DespesaPendente, lerPendentes } from './pendentes';
import { useFinanceStore } from './store';

const RECENTES = 3;

/** O selo de fase no card do total: o que a viagem está vivendo agora. */
function SeloDaFase() {
  const { trip, members } = useTrip();
  const viagem = tripInputDaViagem(trip, members.length);
  const now = useAgora(viagem);
  if (!viagem) return null;
  const fase = tripPhase(viagem, now);
  const faltam = fase === 'antes' ? daysUntilStart(viagem, now) : null;
  const [titulo, sub] =
    fase === 'antes'
      ? ['Preparando a viagem', faltam === 0 ? 'É hoje!' : faltam === 1 ? 'Falta 1 dia' : `Faltam ${faltam} dias`]
      : fase === 'durante'
        ? ['Viagem em andamento', 'Curta cada momento!']
        : ['Viagem encerrada', 'Hora de fechar as contas'];
  return (
    <span className={`fin__selo fin__selo--${fase}`} data-testid="fin-fase">
      <Icon name="aviao" size={16} />
      <span className="fin__selo-texto">
        <span className="fin__selo-titulo">{titulo}</span>
        <span className="fin__selo-sub">{sub}</span>
      </span>
    </span>
  );
}

export function FinanceiroScreen({
  tripId,
  currency,
  meuUserId = '',
}: {
  tripId: string;
  currency: string;
  meuUserId?: string;
}) {
  const navegar = useNavigate();
  const { members } = useTrip();
  const [escopo, setEscopo] = useState<Escopo>('minha');
  const [busca, setBusca] = useState('');
  const [todasCategorias, setTodasCategorias] = useState(false);
  const [todasDespesas, setTodasDespesas] = useState(false);
  const toast = useFinanceStore((s) => s.toast);
  const limparToast = useFinanceStore((s) => s.limparToast);
  // O toast flutua acima da barra (refino final): sai sozinho, como o do roteiro.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(limparToast, 2600);
    return () => window.clearTimeout(t);
  }, [toast, limparToast]);

  /**
   * A fila local entra na lista junto com o que veio do servidor. Sem isto, a
   * despesa registrada offline sumiria da tela até sincronizar — o toast diria
   * "salva" e a lista mostraria o contrário.
   */
  const [pendentes, setPendentes] = useState<DespesaPendente[]>([]);

  useEffect(() => {
    let vivo = true;
    const recarregar = () => {
      void lerPendentes(meuUserId).then((lista) => {
        if (vivo) setPendentes(lista);
      });
    };

    recarregar();
    window.addEventListener('nv:outbox-changed', recarregar);
    return () => {
      vivo = false;
      window.removeEventListener('nv:outbox-changed', recarregar);
    };
  }, [meuUserId]);

  const resumo = useQuery({
    queryKey: ['financeiro', 'resumo', tripId, currency],
    queryFn: () => readSummary(tripId),
  });

  const despesas = useQuery({
    queryKey: ['financeiro', 'despesas', tripId],
    queryFn: () => listExpenses(tripId),
  });

  if (resumo.isLoading || despesas.isLoading) {
    return (
      <section className="fin">
        <h1 className="fin__titulo">Financeiro</h1>
        <Skeleton height={48} raio="control" />
        <Skeleton height={200} raio="hero" />
        <Skeleton height={56} count={3} />
      </section>
    );
  }

  if (resumo.isError || despesas.isError) {
    return (
      <section className="fin">
        <h1 className="fin__titulo">Financeiro</h1>
        <ErrorState
          title="Falha ao sincronizar o financeiro"
          description="As despesas já registradas continuam salvas no aparelho."
          onRetry={() => {
            void resumo.refetch();
            void despesas.refetch();
          }}
        />
        {/* Registrar despesa não depende do servidor: vai para a fila local.
            Esconder o CTA aqui impediria justamente o caso mais comum —
            registrar o gasto na hora, sem sinal. */}
        <button
          type="button"
          className="nv-btn nv-btn--primary nv-btn--block"
          onClick={() => navegar('/financeiro/nova')}
        >
          + Nova despesa
        </button>
      </section>
    );
  }

  // Sair da família enquanto a aba dela está aberta não pode deixar a tela
  // presa num escopo que já não existe.
  const disponiveis = escoposDisponiveis(resumo.data!);
  const escopoAtivo = disponiveis.some((e) => e.key === escopo) ? escopo : 'minha';

  const saldo = saldoParaExibicao(resumo.data!, escopoAtivo);
  const categorias = categoriasParaExibicao(resumo.data!);
  const categoriasVisiveis = todasCategorias || busca ? categorias : categorias.slice(0, RECENTES);
  // A pendente vem primeiro: é a mais recente e é a que a pessoa acabou de
  // registrar.
  const doServidor = despesas.data?.expenses ?? [];
  const naFila = pendentes.filter((p) => !doServidor.some((d) => d.id === p.id));
  const lista = filtrarDespesas([...naFila, ...doServidor], busca);
  const pendenteId = new Set(naFila.map((d) => d.id));
  const listaVisivel = todasDespesas || busca ? lista : lista.slice(0, RECENTES);
  const participantes = new Set(doServidor.map((d) => d.createdBy)).size;

  return (
    <section className="fin">
      <header className="fin__header">
        <h1 className="fin__titulo">Financeiro</h1>
        <span className="fin__header-acoes">
          {naFila.length > 0 && (
            <span className="fin__pendente" role="status">
              {naFila.length === 1 ? '1 pendente' : `${naFila.length} pendentes`}
            </span>
          )}
          {/* Refino final: o atalho do Ranking é a coroa das ilustrações. */}
          <Link className="fin__icone-btn fin__icone-btn--ranking" to="/ranking" aria-label="Ranking da viagem">
            <img src="/ilustracoes/crown-rank.png" alt="" width={24} height={24} />
          </Link>
        </span>
      </header>

      {toast && <Toast texto={toast} />}

      <label className="fin__busca">
        <Icon name="buscar" size={18} />
        <input
          className="fin__busca-input"
          type="search"
          placeholder="Buscar despesa"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          aria-label="Buscar despesa"
        />
      </label>

      <Segmented
        value={escopoAtivo}
        options={disponiveis.map((s) => ({ valor: s.key, rotulo: s.label, testId: `fin-escopo-${s.key}` }))}
        onChange={setEscopo}
        label="Escopo do financeiro"
      />

      <Card variant="raised" className="fin__saldo" data-testid="fin-saldo">
        <div className="fin__saldo-topo">
          <div>
            <div className="fin__saldo-rotulo">
              {escopoAtivo === 'familia' ? (resumo.data!.family?.name ?? 'Família') : 'Total gasto'}
            </div>
            <div className="fin__saldo-valor">{saldo.totalFormatado}</div>
          </div>
          {/* O selo de fase saiu do card (PO, 18/09/2026: poluía). A fase
              já está no cabeçalho da Home; `SeloDaFase` fica para uso futuro. */}
        </div>

        <div className="fin__saldo-cards">
          <div className="fin__saldo-card fin__saldo-card--receber">
            <span className="fin__saldo-card-icone" aria-hidden="true"><Icon name="setaBaixo" size={18} /></span>
            <span className="fin__saldo-card-texto">
              <span className="fin__saldo-card-rotulo">A receber</span>
              <span className="fin__saldo-card-valor">{saldo.aReceberFormatado}</span>
            </span>
          </div>
          <div className="fin__saldo-card">
            <span className="fin__saldo-card-icone" aria-hidden="true"><Icon name="setaCima" size={18} /></span>
            <span className="fin__saldo-card-texto">
              <span className="fin__saldo-card-rotulo">A pagar</span>
              <span className="fin__saldo-card-valor">{saldo.aPagarFormatado}</span>
            </span>
          </div>
        </div>

        <p className="fin__nota">
          {NOTA_DO_ESCOPO[escopoAtivo]}
          {escopoAtivo === 'familia' && resumo.data!.family
            ? ` ${resumo.data!.family.memberCount === 1 ? '1 pessoa na família.' : `${resumo.data!.family.memberCount} pessoas na família.`}`
            : ''}
        </p>
      </Card>

      {categorias.length > 0 && (
        <>
          <div className="fin__secao">
            <span className="nv-section-label">Por categoria</span>
            {categorias.length > RECENTES && !busca && (
              <button type="button" className="fin__ver-todas" onClick={() => setTodasCategorias((v) => !v)} data-testid="fin-ver-categorias">
                {todasCategorias ? 'VER MENOS' : 'VER TODAS'} <Icon name="avancar" size={14} />
              </button>
            )}
          </div>
          <Card variant="surf" lista data-testid="fin-categorias">
            {categoriasVisiveis.map((c) => {
              const cat = categoriaDe(c.chave);
              return (
                <button type="button" className="fin__categoria" key={c.chave} onClick={() => setBusca(busca === c.label ? '' : c.label)} aria-pressed={busca === c.label}>
                  <span className={`fin__cat-icone fin__cat-icone--${cat.tom}`} aria-hidden="true"><Icon name={cat.icone} size={18} /></span>
                  <span className="fin__categoria-corpo">
                    <span className="fin__categoria-nome">{c.label}</span>
                    <span className={`fin__categoria-barra fin__categoria-barra--${cat.tom}`} role="progressbar" aria-valuenow={c.percentual} aria-valuemin={0} aria-valuemax={100} aria-label={`${c.label}: ${c.valorFormatado}`}>
                      <span style={{ width: `${c.percentual}%` }} />
                    </span>
                  </span>
                  <span className="fin__categoria-valor">{c.valorFormatado}</span>
                  <Icon name="avancar" size={16} />
                </button>
              );
            })}
          </Card>
        </>
      )}

      {lista.length === 0 ? (
        <EmptyState
          ilustracao="despesa"
          title={busca ? 'Nada encontrado' : 'Nenhuma despesa ainda'}
          description={
            busca
              ? 'Tente outro termo ou limpe a busca.'
              : 'Registre o primeiro gasto do grupo e o app divide para você.'
          }
          action={
            busca
              ? { label: 'Limpar busca', onClick: () => setBusca('') }
              : { label: '+ Primeira despesa', onClick: () => navegar('/financeiro/nova') }
          }
        />
      ) : (
        <>
          <div className="fin__secao">
            <span className="nv-section-label">{busca ? 'Resultado' : 'Despesas recentes'}</span>
            {lista.length > RECENTES && !busca && (
              <button type="button" className="fin__ver-todas" onClick={() => setTodasDespesas((v) => !v)} data-testid="fin-ver-despesas">
                {todasDespesas ? 'VER MENOS' : 'VER TODAS'} <Icon name="avancar" size={14} />
              </button>
            )}
          </div>
          <Card variant="surf" lista data-testid="fin-despesas">
            {listaVisivel.map((d) => {
              const cat = categoriaDe(d.category);
              return (
                <Link
                  className="fin__linha"
                  key={d.id}
                  // Despesa ainda na fila não tem detalhe no servidor: abrir daria
                  // 404. Ela leva de volta para a própria lista.
                  to={pendenteId.has(d.id) ? '/financeiro' : `/financeiro/${d.id}`}
                >
                  <span className={`fin__linha-icone fin__cat-icone--${cat.tom}`} aria-hidden="true">
                    {(d.description.trim()[0] ?? cat.inicial).toUpperCase()}
                  </span>
                  <span className="fin__linha-texto">
                    <span className="fin__linha-titulo">{d.description}</span>
                    <span className="fin__linha-detalhe">
                      {new Date(d.spentAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                      {' · '}
                      {cat.label}
                      {pendenteId.has(d.id) && ' · pendente de sincronização'}
                    </span>
                  </span>
                  <span className="fin__linha-valor">{formatMoney(d.totalMinor, d.currency)}</span>
                  <Icon name="avancar" size={16} />
                </Link>
              );
            })}
          </Card>
        </>
      )}

      {escopoAtivo !== 'grupo' && doServidor.length > 0 && (
        <button type="button" className="fin__grupo" onClick={() => setEscopo('grupo')} data-testid="fin-resumo-grupo">
          <span className="fin__grupo-icone" aria-hidden="true"><Icon name="pessoas" size={20} /></span>
          <span className="fin__grupo-texto">
            <span className="fin__grupo-titulo">Resumo do grupo</span>
            <span className="fin__grupo-sub">
              {doServidor.length === 1 ? '1 despesa' : `${doServidor.length} despesas`} ·{' '}
              {participantes === 1 ? '1 participante' : `${participantes} participantes`}
              {members.length > participantes ? ` de ${members.length}` : ''}
            </span>
          </span>
          <span className="fin__grupo-total">
            <span className="fin__grupo-rotulo">Total do grupo</span>
            <span className="fin__grupo-valor">{formatMoney(resumo.data!.groupTotalMinor, resumo.data!.currency)}</span>
          </span>
          <Icon name="avancar" size={16} />
        </button>
      )}

      {/* Enquanto não há despesa nenhuma, o estado vazio já oferece
          "+ Primeira despesa": os dois botões de baixo só confundiriam (PO,
          18/09/2026). Com a primeira registrada, voltam. */}
      {lista.length > 0 && (
      <div className="fin__ctas">
        <button
          type="button"
          className="nv-btn nv-btn--secondary"
          onClick={() => navegar('/financeiro/fechar')}
        >
          Fechar contas
        </button>
        <button
          type="button"
          className="nv-btn nv-btn--primary"
          onClick={() => navegar('/financeiro/nova')}
        >
          + Nova despesa
        </button>
      </div>
      )}
    </section>
  );
}
